import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { type ChatHistoryItem, useUser } from '@renderer/chat'
import {
  type ChatHistoryQueryData,
  type ChatMetadataEntry,
  publishChatMetadata,
} from '@renderer/stores/chat-history-cache'
import { CHAT_HISTORY_QUERY_KEY, chatQueryKey } from '@renderer/stores/conversation-query-keys'

export function useChatHistoryStore() {
  const queryClient = useQueryClient()
  const user = useUser()

  const invalidateChatHistory = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [CHAT_HISTORY_QUERY_KEY] })
  }, [queryClient])

  const getHistory = useCallback(() => {
    const paginatedHistory = queryClient.getQueryData<ChatHistoryQueryData>([CHAT_HISTORY_QUERY_KEY])
    return paginatedHistory?.pages.flatMap((page) => page.data) ?? []
  }, [queryClient])

  const addOrUpdateChat = useCallback(
    async (chat: Partial<Omit<ChatHistoryItem, 'id'>> & { id: ChatHistoryItem['id'] }) => {
      const existing = getHistory().find((item) => item.id === chat.id)
      const now = new Date().toISOString()
      const entry: ChatMetadataEntry = {
        ...existing,
        ...chat,
        userId: chat.userId ?? existing?.userId ?? user?.id ?? '',
        createdAt: chat.createdAt ?? existing?.createdAt ?? now,
        updatedAt: chat.updatedAt ?? now,
      }
      publishChatMetadata(queryClient, { [chat.id]: entry })
      // A chat we have never seen usually arrives as a bare id (`chat:created`);
      // its title and shares only exist server-side.
      if (!existing) {
        await queryClient.invalidateQueries({ queryKey: chatQueryKey(chat.id) })
      }
    },
    [getHistory, queryClient, user],
  )

  const removeChatsByIds = useCallback(
    (selectedHistoryIds: Array<ChatHistoryItem['id']>) => {
      publishChatMetadata(queryClient, Object.fromEntries(selectedHistoryIds.map((id) => [id, null])))
    },
    [queryClient],
  )

  return useMemo(
    () => ({ getHistory, invalidateChatHistory, addOrUpdateChat, removeChatsByIds }),
    [getHistory, invalidateChatHistory, addOrUpdateChat, removeChatsByIds],
  )
}
